import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { db } from "../../firebase";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs
} from "firebase/firestore";

function ViewStatsPage() {
  const { gameId } = useParams();

  // State for game info
  const [game, setGame] = useState(null);
  const [loadingGame, setLoadingGame] = useState(true);

  // State for player stats
  const [playerStats, setPlayerStats] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  
  // Sorting state
  const [sortField, setSortField] = useState("points");
  const [sortDirection, setSortDirection] = useState("desc");
  
  // Team filter state
  const [teamFilter, setTeamFilter] = useState("all");

  // Error handling
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!gameId) {
      setError("No gameId provided in the URL.");
      setLoadingGame(false);
      setLoadingStats(false);
      return;
    }

    // Fetch the game document and handle both data models
    const fetchGame = async () => {
      setLoadingGame(true);
      setError(null);
      try {
        const gameRef = doc(db, "games", gameId);
        const gameSnap = await getDoc(gameRef);
        
        if (gameSnap.exists()) {
          const gameData = gameSnap.data();
          let enhancedGameData = { 
            id: gameSnap.id, 
            ...gameData 
          };
          
          // Handle both data models
          if (gameData.yourScore !== undefined) {
            // New data model
            enhancedGameData = {
              ...enhancedGameData,
              // Keep these fields for compatibility with the UI
              homeScore: gameData.yourScore,
              awayScore: gameData.opponentScore
            };
          } else if (gameData.homeTeamId === gameData.teamId) {
            // Old data model - my team is home team
            enhancedGameData = {
              ...enhancedGameData,
              // Add new model fields for consistency
              yourScore: gameData.homeScore,
              opponentScore: gameData.awayScore
            };
          } else if (gameData.awayTeamId === gameData.teamId) {
            // Old data model - my team is away team
            enhancedGameData = {
              ...enhancedGameData,
              // Add new model fields for consistency
              yourScore: gameData.awayScore,
              opponentScore: gameData.homeScore
            };
          }
          
          // Fetch team data if needed
          const enhancedGameWithTeamData = await fetchTeamData(enhancedGameData);
          setGame(enhancedGameWithTeamData);
        } else {
          setError("Game not found.");
        }
      } catch (err) {
        console.error("Error fetching game:", err);
        setError("Failed to fetch game info.");
      }
      setLoadingGame(false);
    };

    // Fetch team data if needed
    const fetchTeamData = async (gameData) => {
      if (!gameData || (!gameData.teamId && !gameData.homeTeamId)) return gameData;
      
      try {
        // Use the appropriate team ID based on data model
        const teamId = gameData.teamId || gameData.homeTeamId;
        const teamRef = doc(db, "teams", teamId);
        const teamSnap = await getDoc(teamRef);
        
        if (teamSnap.exists()) {
          const teamData = teamSnap.data();
          // Return the game data with the team name included
          return {
            ...gameData,
            homeTeam: teamData.name || teamData.teamName || "My Team"
          };
        }
      } catch (err) {
        console.error("Error fetching team data:", err);
      }
      
      return gameData;
    };

    // Fetch playerStats for this game
    const fetchPlayerStats = async () => {
      setLoadingStats(true);
      setError(null);
      try {
        const statsQuery = query(
          collection(db, "playerStats"),
          where("gameId", "==", gameId)
        );
        const statsSnap = await getDocs(statsQuery);

        const statsPromises = statsSnap.docs.map(async (sDoc) => {
          const statsData = sDoc.data();
          // We'll fetch the player's name from the "players" collection
          const playerRef = doc(db, "players", statsData.playerId);
          const playerSnap = await getDoc(playerRef);
          let playerName = "Unknown Player";
          let playerAvatar = null;
          if (playerSnap.exists()) {
            const pData = playerSnap.data();
            playerName = `${pData.firstName || ""} ${pData.lastName || ""}`.trim();
            playerAvatar = pData.avatarUrl || null;
          }
          return {
            id: sDoc.id,
            ...statsData,
            playerName,
            playerAvatar,
          };
        });

        const resolvedStats = await Promise.all(statsPromises);
        setPlayerStats(resolvedStats);
      } catch (err) {
        console.error("Error fetching player stats:", err);
        setError("Failed to fetch player stats.");
      }
      setLoadingStats(false);
    };

    // Main fetch flow with proper sequencing
    const fetchData = async () => {
      try {
        await fetchGame();
        fetchPlayerStats();
      } catch (err) {
        console.error("Error in fetch flow:", err);
        setError("Failed to load game data.");
        setLoadingGame(false);
      }
    };

    fetchData();
  }, [gameId]);

  // Handle sorting
  const handleSort = (field) => {
    setSortDirection(
      field === sortField && sortDirection === "asc" ? "desc" : "asc"
    );
    setSortField(field);
  };

  // Get sorted and filtered stats
  const getSortedAndFilteredStats = () => {
    let filteredStats = [...playerStats];
    
    // Apply team filter
    if (teamFilter !== "all") {
      filteredStats = filteredStats.filter(
        (player) => player.team === teamFilter
      );
    }
    
    // Apply sorting
    filteredStats.sort((a, b) => {
      if (sortDirection === "asc") {
        return a[sortField] - b[sortField];
      } else {
        return b[sortField] - a[sortField];
      }
    });
    
    return filteredStats;
  };

  if (loadingGame) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loading}>Loading game info...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorIcon}>!</div>
        <p style={styles.errorText}>{error}</p>
      </div>
    );
  }

  if (!game) {
    return <p>Game not found.</p>;
  }

  // Format date and time
  const gameDate = game.date ? new Date(game.date).toLocaleDateString("en-US", {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : 'Unknown Date';
  
  // Get scores based on the consistent model
  const yourScore = game.yourScore !== undefined ? game.yourScore : (game.homeScore || 0);
  const opponentScore = game.opponentScore !== undefined ? game.opponentScore : (game.awayScore || 0);
  
  // Determine winner using the consolidated score model
  let winnerText = "It's a tie!";
  let winnerColor = "#6b7280"; // neutral color
  let winnerStyles = { ...styles.winnerBadge, backgroundColor: "#6b7280" };
  
  if (yourScore > opponentScore) {
    winnerText = `${game.homeTeam || 'My Team'} Wins!`;
    winnerColor = "#10b981"; // greenish
    winnerStyles = { ...styles.winnerBadge, backgroundColor: "#10b981" };
  } else if (opponentScore > yourScore) {
    winnerText = `${game.opponentName || 'Opponent'} Wins!`;
    winnerColor = "#ef4444"; // redish
    winnerStyles = { ...styles.winnerBadge, backgroundColor: "#ef4444" };
  }

  return (
    <div style={styles.container}>
      <div style={styles.gameCard}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>Game Summary</h2>
          <div style={winnerStyles}>{winnerText}</div>
        </div>
        
        <div style={styles.scoreboard}>
          <div style={styles.team}>
            <h3 style={styles.teamName}>{game.homeTeam || "My Team"}</h3>
            <div style={styles.scoreBox}>
              <span style={yourScore > opponentScore ? styles.winningScore : styles.score}>
                {yourScore}
              </span>
            </div>
          </div>
          
          <div style={styles.vs}>VS</div>
          
          <div style={styles.team}>
            <h3 style={styles.teamName}>{game.opponentName || "Opponent"}</h3>
            <div style={styles.scoreBox}>
              <span style={opponentScore > yourScore ? styles.winningScore : styles.score}>
                {opponentScore}
              </span>
            </div>
          </div>
        </div>
        
        <div style={styles.gameDetails}>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Date:</span>
            <span style={styles.detailValue}>{gameDate}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Time:</span>
            <span style={styles.detailValue}>{game.time || "N/A"}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Location:</span>
            <span style={styles.detailValue}>{game.locationName || "N/A"}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Address:</span>
            <span style={styles.detailValue}>{game.locationAddress || "N/A"}</span>
          </div>
        </div>
      </div>

      <div style={styles.statsCard}>
        <div style={styles.statsHeader}>
          <h3 style={styles.sectionTitle}>Player Statistics</h3>
          
          <div style={styles.controlsContainer}>
            <div style={styles.filterContainer}>
              <label style={styles.filterLabel}>Filter by Team:</label>
              <select 
                style={styles.filterSelect} 
                value={teamFilter}
                onChange={(e) => setTeamFilter(e.target.value)}
              >
                <option value="all">All Teams</option>
                <option value="home">My Team</option>
                <option value="away">Opponent</option>
              </select>
            </div>
          </div>
        </div>
        
        {loadingStats ? (
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <p style={styles.loading}>Loading player stats...</p>
          </div>
        ) : getSortedAndFilteredStats().length === 0 ? (
          <div style={styles.emptyState}>
            <p style={styles.emptyStateText}>No player stats available for this filter.</p>
          </div>
        ) : (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Player</th>
                  <th 
                    style={styles.th} 
                    onClick={() => handleSort("points")}
                  >
                    <div style={styles.sortableHeader}>
                      Points
                      {sortField === "points" && (
                        <span style={styles.sortIcon}>
                          {sortDirection === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    style={styles.th}
                    onClick={() => handleSort("rebounds")}
                  >
                    <div style={styles.sortableHeader}>
                      Rebounds
                      {sortField === "rebounds" && (
                        <span style={styles.sortIcon}>
                          {sortDirection === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    style={styles.th}
                    onClick={() => handleSort("assists")}
                  >
                    <div style={styles.sortableHeader}>
                      Assists
                      {sortField === "assists" && (
                        <span style={styles.sortIcon}>
                          {sortDirection === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    style={styles.th}
                    onClick={() => handleSort("steals")}
                  >
                    <div style={styles.sortableHeader}>
                      Steals
                      {sortField === "steals" && (
                        <span style={styles.sortIcon}>
                          {sortDirection === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    style={styles.th}
                    onClick={() => handleSort("blocks")}
                  >
                    <div style={styles.sortableHeader}>
                      Blocks
                      {sortField === "blocks" && (
                        <span style={styles.sortIcon}>
                          {sortDirection === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {getSortedAndFilteredStats().map((ps) => (
                  <tr key={ps.id} style={styles.tableRow}>
                    <td style={styles.playerCell}>
                      <div style={styles.playerInfo}>
                        {ps.playerAvatar ? (
                          <img 
                            src={ps.playerAvatar} 
                            alt={ps.playerName} 
                            style={styles.playerAvatar}
                          />
                        ) : (
                          <div style={styles.playerInitials}>
                            {ps.playerName.split(" ").map(n => n[0]).join("")}
                          </div>
                        )}
                        <span style={styles.playerName}>
                          {ps.playerName}
                          <span style={{
                            ...styles.teamBadge,
                            backgroundColor: ps.team === "home" ? "#3b82f6" : "#f97316"
                          }}>
                            {ps.team === "home" ? 
                              (game.homeTeam || "My Team") : 
                              (game.opponentName || "Opponent")}
                          </span>
                        </span>
                      </div>
                    </td>
                    <td style={{
                      ...styles.td,
                      fontWeight: sortField === "points" ? "bold" : "normal",
                      backgroundColor: sortField === "points" ? "#f8fafc" : "transparent"
                    }}>{ps.points}</td>
                    <td style={{
                      ...styles.td,
                      fontWeight: sortField === "rebounds" ? "bold" : "normal",
                      backgroundColor: sortField === "rebounds" ? "#f8fafc" : "transparent"
                    }}>{ps.rebounds}</td>
                    <td style={{
                      ...styles.td,
                      fontWeight: sortField === "assists" ? "bold" : "normal",
                      backgroundColor: sortField === "assists" ? "#f8fafc" : "transparent"
                    }}>{ps.assists}</td>
                    <td style={{
                      ...styles.td,
                      fontWeight: sortField === "steals" ? "bold" : "normal",
                      backgroundColor: sortField === "steals" ? "#f8fafc" : "transparent"
                    }}>{ps.steals}</td>
                    <td style={{
                      ...styles.td,
                      fontWeight: sortField === "blocks" ? "bold" : "normal",
                      backgroundColor: sortField === "blocks" ? "#f8fafc" : "transparent"
                    }}>{ps.blocks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        <div style={styles.statsSummary}>
          <h4 style={styles.summaryTitle}>Game Statistics Summary</h4>
          <div style={styles.statsCards}>
            <div style={styles.statCard}>
              <div style={styles.statValue}>
                {playerStats.reduce((sum, player) => sum + (player.points || 0), 0)}
              </div>
              <div style={styles.statLabel}>Total Points</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statValue}>
                {Math.max(...playerStats.map(player => player.points || 0))}
              </div>
              <div style={styles.statLabel}>Top Scorer</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statValue}>
                {Math.max(...playerStats.map(player => player.rebounds || 0))}
              </div>
              <div style={styles.statLabel}>Top Rebounds</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statValue}>
                {Math.max(...playerStats.map(player => player.assists || 0))}
              </div>
              <div style={styles.statLabel}>Top Assists</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Improved styling
const styles = {
  container: {
    padding: "2rem",
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  gameCard: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "1.5rem",
    marginBottom: "2rem",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1)",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1.5rem",
  },
  cardTitle: {
    fontSize: "1.75rem",
    fontWeight: "700",
    color: "#1e293b",
    margin: 0,
  },
  winnerBadge: {
    padding: "0.5rem 1rem",
    borderRadius: "9999px",
    color: "white",
    fontWeight: "600",
    fontSize: "0.875rem",
  },
  scoreboard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "1.5rem",
    backgroundColor: "#f1f5f9",
    borderRadius: "12px",
    marginBottom: "1.5rem",
  },
  team: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flex: 1,
  },
  teamName: {
    fontSize: "1.25rem",
    fontWeight: "600",
    color: "#334155",
    marginBottom: "1rem",
    textAlign: "center",
  },
  scoreBox: {
    padding: "1rem 1.5rem",
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  },
  score: {
    fontSize: "2.5rem",
    fontWeight: "700",
    color: "#1e293b",
  },
  winningScore: {
    fontSize: "2.5rem",
    fontWeight: "700",
    color: "#10b981",
  },
  vs: {
    fontSize: "1.25rem",
    fontWeight: "700",
    color: "#64748b",
    margin: "0 1rem",
  },
  gameDetails: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "1rem",
    borderTop: "1px solid #e2e8f0",
    paddingTop: "1.5rem",
  },
  detailItem: {
    display: "flex",
    flexDirection: "column",
  },
  detailLabel: {
    fontSize: "0.875rem",
    color: "#64748b",
    marginBottom: "0.25rem",
  },
  detailValue: {
    fontSize: "1rem",
    color: "#1e293b",
    fontWeight: "500",
  },
  statsCard: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "1.5rem",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1)",
  },
  statsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1.5rem",
    flexWrap: "wrap",
    gap: "1rem",
  },
  sectionTitle: {
    fontSize: "1.5rem",
    color: "#1e293b",
    margin: 0,
    fontWeight: "700",
  },
  controlsContainer: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
  },
  filterContainer: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  filterLabel: {
    fontSize: "0.875rem",
    color: "#64748b",
  },
  filterSelect: {
    padding: "0.5rem",
    borderRadius: "6px",
    border: "1px solid #cbd5e1",
    backgroundColor: "white",
    fontSize: "0.875rem",
    color: "#1e293b",
  },
  tableContainer: {
    overflow: "auto",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    textAlign: "left",
  },
  th: {
    backgroundColor: "#f8fafc",
    padding: "1rem",
    color: "#64748b",
    fontWeight: "600",
    fontSize: "0.875rem",
    borderBottom: "1px solid #e2e8f0",
    cursor: "pointer",
    userSelect: "none",
    position: "sticky",
    top: 0,
  },
  sortableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  sortIcon: {
    marginLeft: "0.5rem",
  },
  tableRow: {
    borderBottom: "1px solid #e2e8f0",
    transition: "background-color 0.2s",
    ":hover": {
      backgroundColor: "#f1f5f9",
    },
  },
  td: {
    padding: "1rem",
    color: "#334155",
    fontSize: "0.925rem",
    textAlign: "center",
    transition: "background-color 0.2s",
  },
  playerCell: {
    padding: "0.75rem 1rem",
    textAlign: "left",
  },
  playerInfo: {
    display: "flex",
    alignItems: "center",
  },
  playerAvatar: {
    width: "2.5rem",
    height: "2.5rem",
    borderRadius: "50%",
    objectFit: "cover",
    marginRight: "0.75rem",
  },
  playerInitials: {
    width: "2.5rem",
    height: "2.5rem",
    borderRadius: "50%",
    backgroundColor: "#3b82f6",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "600",
    marginRight: "0.75rem",
  },
  playerName: {
    color: "#1e293b",
    fontWeight: "500",
    display: "flex",
    flexDirection: "column",
    gap: "0.25rem",
  },
  teamBadge: {
    fontSize: "0.75rem",
    padding: "0.125rem 0.5rem",
    borderRadius: "9999px",
    color: "white",
    fontWeight: "500",
    display: "inline-block",
    width: "fit-content",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "3rem 0",
  },
  spinner: {
    border: "4px solid rgba(0, 0, 0, 0.1)",
    borderLeftColor: "#3b82f6",
    borderRadius: "50%",
    width: "2rem",
    height: "2rem",
    animation: "spin 1s linear infinite",
    marginBottom: "1rem",
  },
  loading: {
    color: "#64748b",
    fontSize: "1rem",
  },
  errorContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "2rem",
    backgroundColor: "#fee2e2",
    borderRadius: "8px",
    margin: "2rem 0",
  },
  errorIcon: {
    width: "2rem",
    height: "2rem",
    borderRadius: "50%",
    backgroundColor: "#ef4444",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
    fontSize: "1.25rem",
    marginRight: "1rem",
  },
  errorText: {
    color: "#991b1b",
    fontSize: "1rem",
    margin: 0,
  },
  emptyState: {
    padding: "3rem 0",
    textAlign: "center",
  },
  emptyStateText: {
    color: "#64748b",
    fontSize: "1rem",
  },
  statsSummary: {
    marginTop: "2rem",
    padding: "1.5rem",
    backgroundColor: "#f8fafc",
    borderRadius: "12px",
  },
  summaryTitle: {
    fontSize: "1.125rem",
    color: "#1e293b",
    marginBottom: "1rem",
    fontWeight: "600",
  },
  statsCards: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: "1rem",
  },
  statCard: {
    backgroundColor: "white",
    padding: "1rem",
    borderRadius: "8px",
    textAlign: "center",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
  },
  statValue: {
    fontSize: "1.5rem",
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: "0.25rem",
  },
  statLabel: {
    fontSize: "0.875rem",
    color: "#64748b",
  },
};

export default ViewStatsPage;